<script lang="ts">
import type { Component } from 'vue'
import type { StatValueVariants, StatVariants } from '#ui-kit/ui/stat'

export interface KpiStatAction {
  icon: Component
  label: string
}

export interface KpiStatProps {
  title?: string
  tooltip?: string
  value?: string | number | null
  description?: string
  meta?: string | number
  loading?: boolean
  action?: KpiStatAction
  variant?: StatVariants['variant']
  align?: StatVariants['align']
  size?: StatValueVariants['size']
}
</script>

<script setup lang="ts">
import { QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import { computed } from 'vue'
import { Button } from '#ui-kit/ui/button'
import { Skeleton } from '#ui-kit/ui/skeleton'
import { Stat, StatContent, StatDescription, StatFooter, StatHeader, StatLabel, StatValue } from '#ui-kit/ui/stat'
import { Tooltip, TooltipContent, TooltipTrigger } from '#ui-kit/ui/tooltip'

const props = withDefaults(defineProps<KpiStatProps>(), {
  value: undefined,
  meta: undefined,
  variant: 'card',
})

const emit = defineEmits<{
  action: []
}>()

const slots = defineSlots<{
  icon?: () => unknown
  value?: () => unknown
  description?: () => unknown
}>()

const hasMeta = computed(() => props.meta !== undefined && props.meta !== '')
// "Last report: 12 Sep" when there is a figure, "Last report" when there is none
const descriptionText = computed(() =>
  props.description && hasMeta.value ? `${props.description}:` : props.description,
)

// Slot presence can change between renders (a consumer's `<template v-if #description>`),
// so it is read at render time rather than cached in a computed.
function hasDescription() {
  return Boolean(slots.description || props.description)
}
</script>

<template>
  <Stat :variant="variant" :align="align">
    <StatHeader>
      <StatLabel>{{ title }}</StatLabel>
      <Tooltip v-if="tooltip">
        <TooltipTrigger as-child>
          <QuestionMarkCircleIcon class="size-4 shrink-0 text-accent" aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent>{{ tooltip }}</TooltipContent>
      </Tooltip>
    </StatHeader>

    <Skeleton v-if="loading" class="h-6 w-full rounded-lg" />
    <StatContent v-else>
      <slot name="value">
        <slot name="icon" />
        <StatValue :size="size" class="truncate">{{ value }}</StatValue>
      </slot>
    </StatContent>

    <StatFooter v-if="hasDescription() || action">
      <Skeleton
        v-if="loading && hasDescription()"
        class="h-3 w-24 rounded group-data-[align=center]/stat:mx-auto group-data-[variant=joined]/stat-group:mx-auto"
      />
      <StatDescription v-else-if="hasDescription()">
        <slot name="description">
          <span>{{ descriptionText }}</span>
          <span v-if="hasMeta" class="font-semibold">{{ meta }}</span>
        </slot>
      </StatDescription>
      <Tooltip v-if="action && !loading">
        <TooltipTrigger as-child>
          <Button variant="outline" size="xs" icon-only :aria-label="action.label" @click="emit('action')">
            <component :is="action.icon" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{{ action.label }}</TooltipContent>
      </Tooltip>
    </StatFooter>
  </Stat>
</template>
